import React, { useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Divider,
  Tag,
  Space,
  Spin,
  Alert,
  Typography,
  Timeline,
  Badge
} from 'antd'
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHandHoldingMedical,
  faUserInjured,
  faMoneyBillWave,
  faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { formatCurrency } from '../../utils/helpers'
import { getPatientLedgerSummary, getPatientLedgerTransactions } from '../../backend-integration/api'

const { Title, Text } = Typography

const AccountBalanceSummary = ({ patientId, accountData, onRefresh }) => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [recentTransactions, setRecentTransactions] = useState([])
  const [medicalAidCoverage, setMedicalAidCoverage] = useState({
    totalBilled: 0,
    coveredAmount: 0,
    approvedAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
    rejectedAmount: 0,
    coveragePercentage: 0
  })

  useEffect(() => {
    if (patientId) {
      loadAccountSummary()
    }
  }, [patientId, accountData?.billingId])

  const loadAccountSummary = async () => {
    setLoading(true)
    try {
      // Get ledger summary - use billingId as primary identifier
      const billingId = accountData?.billingId
      const summaryData = await getPatientLedgerSummary(billingId, patientId)
      setSummary(summaryData)

      // Get recent transactions - use billingId as primary identifier
      const transactions = await getPatientLedgerTransactions({
        billingId: billingId,
        limit: 10,
        sort: { date: -1 }
      })
      setRecentTransactions(transactions)

      // Calculate medical aid coverage
      calculateMedicalAidCoverage(transactions)
    } catch (error) {
      console.error('Failed to load account summary:', error)
    } finally {
      setLoading(false)
    }
  }

  const calculateMedicalAidCoverage = (transactions) => {
    const invoices = transactions.filter((t) => t.type === 'invoice')
    const approvals = transactions.filter((t) => t.type === 'medical_aid_approval')
    const payments = transactions.filter((t) => t.type === 'medical_aid_payment')

    const totalBilled = invoices.reduce((sum, inv) => sum + (inv.medicalAidAmount || 0), 0)
    const approvedAmount = approvals.reduce((sum, app) => sum + (app.medicalAidAmount || 0), 0)
    const paidAmount = payments.reduce((sum, pay) => sum + (pay.credit || 0), 0)
    const pendingAmount = approvedAmount - paidAmount

    setMedicalAidCoverage({
      totalBilled,
      coveredAmount: totalBilled,
      approvedAmount,
      paidAmount,
      pendingAmount,
      rejectedAmount: 0, // Would need rejection tracking
      coveragePercentage: totalBilled > 0 ? (paidAmount / totalBilled) * 100 : 0
    })
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size='large' />
        </div>
      </Card>
    )
  }

  if (!summary) {
    return (
      <Card>
        <Alert
          message='No Account Data'
          description='No financial data available for this patient'
          type='info'
          showIcon
        />
      </Card>
    )
  }

  // Helper function to detect overpayment issues
  const getOverpaymentAlert = () => {
    if (!summary) return null

    const patientBalance = summary.patientBalance || 0
    const medicalAidBalance = summary.medicalAidBalance || 0
    const compensationBalance = summary.compensationBalance || 0
    const totalBalance = summary.currentBalance || 0

    // Check for overpayment scenarios
    let alertMessage = null
    let overpaymentAmount = 0
    let alertType = 'error'

    // Patient overpaid while medical aid balance exists
    if (patientBalance < 0 && medicalAidBalance > 0) {
      overpaymentAmount = Math.abs(patientBalance)
      alertMessage = {
        title: '⚠️ Patient Account Overpayment Detected',
        description: (
          <div>
            <p>
              <strong>Patient has overpaid by {formatCurrency(overpaymentAmount)}</strong>
            </p>
            <p>
              • Patient balance: <span style={{ color: '#52c41a' }}>{formatCurrency(patientBalance)}</span> (credit)
            </p>
            <p>
              • Medical aid balance: <span style={{ color: '#ff4d4f' }}>{formatCurrency(medicalAidBalance)}</span>{' '}
              (outstanding)
            </p>
            <p>
              <strong>Action Required:</strong> Transfer{' '}
              {formatCurrency(Math.min(overpaymentAmount, medicalAidBalance))} from patient credit to medical aid
              portion, or process a refund.
            </p>
          </div>
        )
      }
    }
    // Medical aid overpaid while patient balance exists
    else if (medicalAidBalance < 0 && patientBalance > 0) {
      overpaymentAmount = Math.abs(medicalAidBalance)
      alertMessage = {
        title: '⚠️ Medical Aid Account Overpayment Detected',
        description: (
          <div>
            <p>
              <strong>Medical aid has overpaid by {formatCurrency(overpaymentAmount)}</strong>
            </p>
            <p>
              • Medical aid balance: <span style={{ color: '#52c41a' }}>{formatCurrency(medicalAidBalance)}</span>{' '}
              (credit)
            </p>
            <p>
              • Patient balance: <span style={{ color: '#ff4d4f' }}>{formatCurrency(patientBalance)}</span>{' '}
              (outstanding)
            </p>
            <p>
              <strong>Action Required:</strong> Transfer {formatCurrency(Math.min(overpaymentAmount, patientBalance))}{' '}
              from medical aid credit to patient portion.
            </p>
          </div>
        )
      }
    }
    // Overall account credit (total overpayment)
    else if (totalBalance < 0) {
      overpaymentAmount = Math.abs(totalBalance)
      alertMessage = {
        title: '💰 Account Credit Balance',
        description: (
          <div>
            <p>
              <strong>Account has a credit balance of {formatCurrency(overpaymentAmount)}</strong>
            </p>
            <p>This account has been overpaid and shows a credit balance.</p>
            <p>
              <strong>Options:</strong> Process refund, apply to future invoices, or leave as account credit.
            </p>
          </div>
        )
      }
      alertType = 'warning'
    }

    if (alertMessage) {
      return (
        <Alert
          message={alertMessage.title}
          description={alertMessage.description}
          type={alertType}
          showIcon
          style={{
            marginBottom: 16,
            border: alertType === 'error' ? '2px solid #ff4d4f' : '2px solid #faad14',
            backgroundColor: alertType === 'error' ? '#fff2f0' : '#fffbf0'
          }}
          action={
            <Space direction='vertical'>
              <Text strong style={{ color: alertType === 'error' ? '#ff4d4f' : '#fa8c16' }}>
                Amount: {formatCurrency(overpaymentAmount)}
              </Text>
            </Space>
          }
        />
      )
    }

    return null
  }

  return (
    <div>
      {/* Overpayment Warning Alert */}
      {getOverpaymentAlert()}

      {/* Account Overview */}
      <Card title='Account Overview' style={{ marginBottom: 16 }}>
        <Row gutter={[12, 16]}>
          <Col span={5}>
            <Statistic
              title='Total Outstanding'
              value={summary.currentBalance}
              precision={2}
              prefix='R'
              valueStyle={{
                color: summary.currentBalance > 0 ? '#ff4d4f' : '#52c41a',
                fontSize: 24
              }}
              suffix={
                summary.currentBalance > 0 ? (
                  <FallOutlined style={{ fontSize: 14 }} />
                ) : (
                  <CheckCircleOutlined style={{ fontSize: 14 }} />
                )
              }
            />
          </Col>
          <Col span={5}>
            <Statistic
              title='Patient Portion'
              value={summary.patientBalance}
              precision={2}
              prefix={<FontAwesomeIcon icon={faUserInjured} style={{ marginRight: 6, fontSize: 14 }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title='Medical Aid Portion'
              value={summary.medicalAidBalance}
              precision={2}
              prefix={<FontAwesomeIcon icon={faHandHoldingMedical} style={{ marginRight: 6, fontSize: 14 }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={5}>
            <Statistic
              title='Compensation Fund'
              value={summary.compensationBalance || 0}
              precision={2}
              prefix={<FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: 6, fontSize: 14 }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title='Total Payments'
              value={summary.totalCredits}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>

        <Divider />

        {/* Payment Summary */}
        <Title level={5}>Payment Summary</Title>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Progress
            percent={summary.totalDebits > 0 ? Math.round((summary.totalCredits / summary.totalDebits) * 100) : 0}
            status='active'
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068'
            }}
            format={(percent) => `${percent}% Paid`}
          />
          <Row justify='space-between'>
            <Text>Total Billed</Text>
            <Text strong>{formatCurrency(summary.totalDebits)}</Text>
          </Row>
          <Row justify='space-between'>
            <Text>Total Paid</Text>
            <Text strong style={{ color: '#52c41a' }}>
              {formatCurrency(summary.totalCredits)}
            </Text>
          </Row>
          <Row justify='space-between'>
            <Text>Outstanding</Text>
            <Text strong style={{ color: '#ff4d4f' }}>
              {formatCurrency(summary.currentBalance)}
            </Text>
          </Row>
        </Space>
      </Card>

      {/* Medical Aid Coverage Analysis */}
      {accountData?.medicalAid && accountData.medicalAid !== 'Private' && (
        <Card title='Medical Aid Coverage Analysis' style={{ marginBottom: 16 }}>
          <Space direction='vertical' style={{ width: '100%' }}>
            <Alert
              message={`Coverage Rate: ${medicalAidCoverage.coveragePercentage.toFixed(1)}%`}
              description={`Medical aid has paid ${formatCurrency(medicalAidCoverage.paidAmount)} of ${formatCurrency(
                medicalAidCoverage.totalBilled
              )} billed`}
              type={medicalAidCoverage.coveragePercentage >= 80 ? 'success' : 'warning'}
              showIcon
            />

            <Timeline>
              <Timeline.Item color='blue' dot={<FileTextOutlined />}>
                <Text>Billed to Medical Aid</Text>
                <br />
                <Text strong>{formatCurrency(medicalAidCoverage.totalBilled)}</Text>
              </Timeline.Item>

              {medicalAidCoverage.approvedAmount > 0 && (
                <Timeline.Item color='cyan' dot={<CheckCircleOutlined />}>
                  <Text>Approved by Medical Aid</Text>
                  <br />
                  <Text strong>{formatCurrency(medicalAidCoverage.approvedAmount)}</Text>
                </Timeline.Item>
              )}

              {medicalAidCoverage.pendingAmount > 0 && (
                <Timeline.Item color='orange' dot={<ClockCircleOutlined />}>
                  <Text>Awaiting Payment</Text>
                  <br />
                  <Text strong>{formatCurrency(medicalAidCoverage.pendingAmount)}</Text>
                </Timeline.Item>
              )}

              {medicalAidCoverage.paidAmount > 0 && (
                <Timeline.Item color='green' dot={<DollarOutlined />}>
                  <Text>Paid by Medical Aid</Text>
                  <br />
                  <Text strong>{formatCurrency(medicalAidCoverage.paidAmount)}</Text>
                </Timeline.Item>
              )}
            </Timeline>
          </Space>
        </Card>
      )}

      {/* Recent Activity */}
      <Card title='Recent Transactions' style={{ marginBottom: 16 }}>
        <Timeline>
          {recentTransactions.slice(0, 5).map((transaction, index) => {
            const isCredit = transaction.credit > 0
            const amount = isCredit ? transaction.credit : transaction.debit

            return (
              <Timeline.Item
                key={transaction._id || index}
                color={isCredit ? 'green' : 'red'}
                dot={isCredit ? <RiseOutlined /> : <FallOutlined />}
              >
                <Row justify='space-between' align='middle'>
                  <Col span={16}>
                    <Text>{transaction.description}</Text>
                    <br />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {dayjs(transaction.date).format('DD MMM YYYY')}
                      {transaction.invoiceNo && ` • Invoice: ${transaction.invoiceNo}`}
                    </Text>
                  </Col>
                  <Col span={8} style={{ textAlign: 'right' }}>
                    <Text strong style={{ color: isCredit ? '#52c41a' : '#ff4d4f' }}>
                      {isCredit ? '+' : '-'} {formatCurrency(amount)}
                    </Text>
                  </Col>
                </Row>
              </Timeline.Item>
            )
          })}
        </Timeline>
      </Card>

      {/* Account Status Summary */}
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title='Account Age'
              value={accountData?.daysOutstanding || 0}
              suffix='days'
              prefix={<ClockCircleOutlined />}
              valueStyle={{
                color: accountData?.daysOutstanding > 60 ? '#ff4d4f' : '#52c41a'
              }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title='Last Payment'
              value={summary.lastPaymentDate ? dayjs(summary.lastPaymentDate).format('DD MMM') : 'No payments'}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title='Account Status'
              value={accountData?.status || 'Active'}
              valueStyle={{ fontSize: 16 }}
              formatter={(value) => (
                <Tag color={getStatusColor(value)} style={{ fontSize: 14 }}>
                  {getStatusLabel(value)}
                </Tag>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

const getStatusColor = (status) => {
  const colors = {
    active: 'green',
    awaiting_payment_patient: 'orange',
    awaiting_payment_aid: 'blue',
    disputed: 'red',
    completed: 'green',
    payment_plan: 'purple'
  }
  return colors[status] || 'default'
}

const getStatusLabel = (status) => {
  const labels = {
    active: 'Active',
    awaiting_payment_patient: 'Awaiting Patient Payment',
    awaiting_payment_aid: 'Awaiting Medical Aid',
    disputed: 'Disputed',
    completed: 'Completed',
    payment_plan: 'Payment Plan'
  }
  return labels[status] || status
}

export default AccountBalanceSummary
